import { Tooltip, type TooltipProps } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { LocateFixed } from 'lucide-react';
import { ReactNode } from 'react';
import { OverlayLabel } from '~/components/OverlayLabel';
import { OverlayActionButton } from './OverlayActionButton';

/**
 * The go-to-maximum control of the PET and dose colorwash overlays.
 */
interface NavigateToMaxButtonProps {
    /**
     * Tooltip text and accessible name. Intentionally the same string —
     * the visible tooltip and the assistive-tech label describe the same
     * action (e.g. "Focus all viewports on SUVmax (4.2 SUV)").
     */
    label: string;
    /** Caption shown next to the locate icon, e.g. "Max: 4.2 SUV". */
    children: ReactNode;
    onClick: () => void;
    /**
     * Apply the glass backdrop. Enable when the button floats directly
     * over the image (PET overlay); leave off when it already sits inside
     * a glass panel (dose overlay) to avoid stacking two backdrops.
     */
    glass?: boolean;
    /**
     * Which way the tooltip opens. Never downward: the colorwash slider is
     * the next element in the column. An overlay docked at the pane's right
     * edge passes `left`.
     */
    tooltipPlacement?: TooltipProps['placement'];
    /** Extra positioning overrides (margins, etc.). */
    sx?: SxProps<Theme>;
    testId?: string;
}

export const NavigateToMaxButton = ({
    label,
    children,
    onClick,
    glass = false,
    tooltipPlacement = 'right',
    sx,
    testId,
}: NavigateToMaxButtonProps) => (
    <Tooltip title={label} placement={tooltipPlacement}>
        <OverlayActionButton
            focusRipple
            onClick={onClick}
            aria-label={label}
            data-testid={testId}
            sx={[
                glass && ((theme: Theme) => ({ ...theme.mixins.glassOverlay })),
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            <LocateFixed size={12} aria-hidden />
            <OverlayLabel>{children}</OverlayLabel>
        </OverlayActionButton>
    </Tooltip>
);

export default NavigateToMaxButton;
